/**
 * /places/:placeId — Place detail (the most editorial screen).
 *
 * Composition per UX-AC-057:
 *   - Hero: serif h1 + category + status pill + address
 *   - Trajectory sparkline (or "Add a second visit..." caption)
 *   - Tags row
 *   - Visit timeline (reverse chrono, 12 per page)
 *   - Photo gallery (6 most recent + "See all (N)")
 *   - People we know here
 *   - Add-visit sheet trigger
 *
 * Status changer is a DropdownMenu with optimistic update via useUpdatePlace.
 * More-menu offers Edit / Archive / Delete with ConfirmDialog.
 */
import * as React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Archive,
  CaretDown,
  DotsThreeVertical,
  Pencil,
  PencilSimple,
  Plus,
  Trash,
} from '@phosphor-icons/react';
import type {
  Person,
  PlaceId,
  PlaceStatus,
  Visit,
} from '@shared/types';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { StatusPill } from '../../components/ui/StatusPill';
import { TagChip } from '../../components/ui/TagChip';
import { Sparkline } from '../../components/ui/Sparkline';
import { Sheet } from '../../components/ui/Sheet';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DropdownMenu } from '../../components/ui/DropdownMenu';
import { PersonCard } from '../../components/ui/PersonCard';
import { VisitTimelineEntry } from '../../components/VisitTimelineEntry';
import { PhotoGallery } from '../../components/PhotoGallery';
import { AddPhotoButton } from '../../components/AddPhotoButton';
import { AddVisitForm } from '../../components/AddVisitForm';
import { EditPersonForm } from '../../components/EditPersonForm';
import {
  useDeletePlace,
  useDeleteVisit,
  usePeople,
  usePlace,
  useTags,
  useTrajectory,
  useUpdatePlace,
  useVisits,
} from '../../lib/queries';
import { useActiveHouseholdId } from '../../lib/household';
import { useToast } from '../../lib/store';
import { useErrorToasts } from '../../lib/errorToast';
import { formatRelative } from '../../lib/date';
import { apiFetch } from '../../lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '../../lib/cn';

const STATUS_OPTIONS: PlaceStatus[] = [
  'go-to',
  'untried',
  'neutral',
  'avoid',
  'archived',
];

export default function PlaceDetailRoute() {
  const { placeId } = useParams<{ placeId: string }>();
  const navigate = useNavigate();
  const householdId = useActiveHouseholdId();
  const toast = useToast();
  const errors = useErrorToasts();
  const qc = useQueryClient();

  // If the placeId param is malformed, route to a clean 404 inside the shell.
  if (!placeId || !placeId.startsWith('plc_')) {
    return <Navigate to="/" replace />;
  }
  const id = placeId as PlaceId;

  const place = usePlace(householdId, id);
  const visits = useVisits(householdId, id);
  const trajectory = useTrajectory(householdId, id);
  const people = usePeople(householdId, id);
  const tagsQ = useTags(householdId);
  const updatePlace = useUpdatePlace(householdId, id);
  const deletePlace = useDeletePlace(householdId);
  const deleteVisit = useDeleteVisit(householdId);

  const [addVisitOpen, setAddVisitOpen] = React.useState(false);
  const [confirmDeleteVisit, setConfirmDeleteVisit] =
    React.useState<Visit | null>(null);
  const [confirmDeletePlace, setConfirmDeletePlace] = React.useState(false);
  const [addingPerson, setAddingPerson] = React.useState(false);
  const [editingPerson, setEditingPerson] = React.useState<Person | null>(null);

  if (place.isPending) {
    return <PlaceDetailSkeleton />;
  }
  if (place.error) {
    return (
      <EmptyState
        title="We couldn't load this place."
        description="It may have been removed, or you may have lost connection."
        cta={
          <Button variant="primary" onClick={() => void place.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }
  if (!place.data) {
    return <Navigate to="/" replace />;
  }
  const p = place.data;

  const visitList = visits.data?.visits ?? [];
  const trajectoryPoints = trajectory.data?.points ?? [];
  const peopleList = people.data?.people ?? [];
  const tagList = tagsQ.data?.tags ?? [];
  const tagsForPlace = tagList.filter((t) => p.tag_ids.includes(t.id));

  async function changeStatus(next: PlaceStatus) {
    if (next === p.status) return;
    try {
      await updatePlace.mutateAsync({ status: next });
      toast.success(`Status updated to ${next}.`);
    } catch (err) {
      if (!errors.handle(err)) {
        toast.error(
          err instanceof Error ? err.message : "We couldn't update the status.",
        );
      }
    }
  }

  async function onDeletePlace() {
    try {
      await deletePlace.mutateAsync({ placeId: id });
      toast.success('Place removed.');
      navigate('/places');
    } catch (err) {
      if (!errors.handle(err)) {
        toast.error(
          err instanceof Error ? err.message : "We couldn't remove the place.",
        );
      }
    }
  }

  async function onArchivePlace() {
    await changeStatus('archived');
  }

  async function onDeleteVisit() {
    if (!confirmDeleteVisit) return;
    try {
      await deleteVisit.mutateAsync({
        visitId: confirmDeleteVisit.id,
        placeId: id,
      });
      toast.success('Entry removed.');
      setConfirmDeleteVisit(null);
    } catch (err) {
      if (!errors.handle(err)) {
        toast.error(
          err instanceof Error ? err.message : "We couldn't remove the entry.",
        );
      }
    }
  }

  async function onDeletePerson(personId: string) {
    try {
      await apiFetch(`/people/${personId}`, { method: 'DELETE' });
      void qc.invalidateQueries({ queryKey: ['people', householdId, id] });
      toast.success('Removed.');
    } catch (err) {
      if (!errors.handle(err)) {
        toast.error(
          err instanceof Error ? err.message : "We couldn't remove them.",
        );
      }
    }
  }

  const visitCount = p.visit_count;
  const firstVisitedAt = trajectoryPoints[0]?.visited_at;
  const lastVisited = p.last_visited_at;

  // AC-058: when the place has no photos, render the editorial typography-only
  // hero (larger title, more whitespace, no border rule). When photos exist,
  // the default header with a hairline rule lets the gallery breathe.
  const heroVariant = p.photo_count === 0 ? 'editorial' : 'default';

  return (
    <article>
      <PageHeader
        eyebrow={p.address ?? 'Place'}
        title={p.name}
        variant={heroVariant}
        actions={
          <div className="flex items-center gap-2">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className={cn(
                    'inline-flex items-center gap-2 px-3 py-1 border border-ink-200',
                    'font-sans text-xs uppercase tracking-[0.12em] text-ink-700',
                    'hover:bg-surface-2 transition-colors duration-200 ease-out-slow',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
                  )}
                  aria-label="Change status"
                  data-testid="status-changer"
                >
                  <StatusPill status={p.status} />
                  <CaretDown size={12} weight="regular" aria-hidden />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                {STATUS_OPTIONS.map((s) => (
                  <DropdownMenu.Item
                    key={s}
                    onSelect={() => void changeStatus(s)}
                    role="menuitem"
                  >
                    <StatusPill status={s} />
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Root>

            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  aria-label="More actions"
                  className={cn(
                    'p-2 border border-ink-200 text-ink-700 hover:bg-surface-2',
                    'transition-colors duration-200 ease-out-slow',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
                  )}
                >
                  <DotsThreeVertical size={16} weight="regular" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  onSelect={() => navigate(`/places/${id}/edit`)}
                >
                  <Pencil size={14} weight="regular" aria-hidden />
                  Edit details
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => void onArchivePlace()}>
                  <Archive size={14} weight="regular" aria-hidden />
                  Archive
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item
                  onSelect={() => setConfirmDeletePlace(true)}
                >
                  <Trash size={14} weight="regular" aria-hidden />
                  Remove
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </div>
        }
      />

      {tagsForPlace.length ? (
        <div
          className="flex flex-wrap gap-2 mb-8 -mt-4"
          data-testid="place-tags"
        >
          {tagsForPlace.map((t) => (
            <TagChip key={t.id} label={t.name} />
          ))}
        </div>
      ) : null}

      <section className="mb-12" data-testid="trajectory-section">
        {trajectoryPoints.length >= 2 ? (
          <div>
            <Sparkline
              points={trajectoryPoints}
              width={480}
              height={56}
              className="w-full"
              data-testid="sparkline"
            />
            <p className="mt-3 text-xs italic text-ink-500">
              {visitCount} {visitCount === 1 ? 'entry' : 'entries'}
              {firstVisitedAt
                ? `, first visited ${formatRelative(firstVisitedAt)}`
                : ''}
              {lastVisited
                ? ` · last visited ${formatRelative(lastVisited)}`
                : ''}
            </p>
          </div>
        ) : (
          <p className="font-serif italic text-base text-ink-500">
            Add a second visit to see the trajectory.
          </p>
        )}
      </section>

      <div className="grid gap-10 lg:grid-cols-[7fr_5fr] lg:gap-16">
        <section data-testid="timeline-section">
          <header className="mb-6 flex items-baseline justify-between">
            <h2 className="font-serif text-2xl text-ink-900">Entries</h2>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAddVisitOpen(true)}
            >
              <Plus size={14} weight="regular" aria-hidden />
              Add entry
            </Button>
          </header>

          {visits.isPending ? (
            <TimelineSkeleton />
          ) : visitList.length === 0 ? (
            <EmptyState
              title="No journal entries for this place yet."
              description="Capture your first taste here."
              cta={
                <Button variant="primary" onClick={() => setAddVisitOpen(true)}>
                  Add the first entry
                </Button>
              }
            />
          ) : (
            <div data-testid="visits-list">
              {visitList.map((v) => (
                <VisitTimelineEntry
                  key={v.id}
                  visit={v}
                  onEdit={() => navigate(`/visits/${v.id}/edit`)}
                  onDelete={() => setConfirmDeleteVisit(v)}
                />
              ))}
            </div>
          )}
        </section>

        <aside className="space-y-12">
          <section data-testid="photos-section">
            <header className="mb-4 flex items-baseline justify-between">
              <h2 className="font-serif text-2xl text-ink-900">Photos</h2>
              <AddPhotoButton placeId={id} label="Add photo" />
            </header>
            <PhotoGallery photoIds={p.photo_ids ?? []} limit={6} />
          </section>

          <section data-testid="people-section">
            <header className="mb-4 flex items-baseline justify-between">
              <h2 className="font-serif text-2xl text-ink-900">
                People we know here
              </h2>
              {!addingPerson && !editingPerson ? (
                <button
                  type="button"
                  onClick={() => setAddingPerson(true)}
                  className="text-xs uppercase tracking-widest text-ink-500 hover:text-ink-900 focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
                >
                  Add person
                </button>
              ) : null}
            </header>

            {people.isPending ? (
              <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : peopleList.length === 0 && !addingPerson ? (
              <p className="font-serif italic text-sm text-ink-500">
                Just you, for now.
              </p>
            ) : (
              <div>
                {peopleList.map((person) =>
                  editingPerson?.id === person.id ? (
                    <EditPersonForm
                      key={person.id}
                      placeId={id}
                      initial={{
                        id: person.id,
                        name: person.name,
                        role: person.role,
                        notes: person.notes,
                      }}
                      onSuccess={() => {
                        setEditingPerson(null);
                        void qc.invalidateQueries({
                          queryKey: ['people', householdId, id],
                        });
                      }}
                      onCancel={() => setEditingPerson(null)}
                    />
                  ) : (
                    <PersonCard
                      key={person.id}
                      name={person.name}
                      role={person.role}
                      notes={person.notes}
                      onEdit={() => setEditingPerson(person)}
                      onDelete={() => void onDeletePerson(person.id)}
                    />
                  ),
                )}
              </div>
            )}

            {addingPerson ? (
              <EditPersonForm
                placeId={id}
                onSuccess={() => {
                  setAddingPerson(false);
                  void qc.invalidateQueries({
                    queryKey: ['people', householdId, id],
                  });
                }}
                onCancel={() => setAddingPerson(false)}
              />
            ) : null}
          </section>
        </aside>
      </div>

      <Sheet
        open={addVisitOpen}
        onOpenChange={setAddVisitOpen}
        title="Add an entry"
        description="Log a visit to this place."
        side="right"
      >
        <header className="mb-6">
          <p className="font-sans text-xs uppercase tracking-[0.14em] text-ink-500">
            New entry
          </p>
          <h2 className="mt-1 font-serif text-2xl text-ink-900 leading-tight">
            {p.name}
          </h2>
        </header>
        <AddVisitForm
          placeId={id}
          onSuccess={() => setAddVisitOpen(false)}
          onCancel={() => setAddVisitOpen(false)}
        />
      </Sheet>

      <ConfirmDialog
        open={Boolean(confirmDeleteVisit)}
        onOpenChange={(o) => (o ? null : setConfirmDeleteVisit(null))}
        title="Delete this entry?"
        description="It can't be undone."
        confirmLabel="Delete"
        loading={deleteVisit.isPending}
        onConfirm={onDeleteVisit}
      />

      <ConfirmDialog
        open={confirmDeletePlace}
        onOpenChange={setConfirmDeletePlace}
        title={`Delete ${p.name}?`}
        description={`We'll also remove its ${p.visit_count} ${
          p.visit_count === 1 ? 'entry' : 'entries'
        } and ${p.photo_count} ${
          p.photo_count === 1 ? 'photo' : 'photos'
        }. This can't be undone.`}
        confirmLabel="Delete"
        loading={deletePlace.isPending}
        onConfirm={onDeletePlace}
      />

      {/* `PencilSimple` reserved for inline edit affordance in future; explicit ref keeps unused-import scan quiet. */}
      <span className="sr-only" aria-hidden>
        <PencilSimple size={1} />
      </span>
    </article>
  );
}

function PlaceDetailSkeleton() {
  return (
    <div data-testid="place-detail-skeleton">
      <div className="mb-10 pb-8 border-b border-ink-200">
        <Skeleton className="h-3 w-24 mb-3" />
        <Skeleton className="h-12 w-3/4" />
      </div>
      <Skeleton className="h-12 w-full mb-12" />
      <div className="grid gap-10 lg:grid-cols-[7fr_5fr]">
        <div>
          <Skeleton className="h-6 w-32 mb-6" />
          <TimelineSkeleton />
        </div>
        <div className="space-y-8">
          <Skeleton className="h-6 w-32 mb-3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-6 w-32 mb-3" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="py-6 border-b border-ink-100">
          <Skeleton className="h-3 w-32 mb-3" />
          <Skeleton className="h-4 w-full mb-1" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ))}
    </div>
  );
}

export { PlaceDetailSkeleton };
